// src/pages/SystemAISettings.js — Cấu hình OpenAI ChatGPT + Knowledge Base + Custom Models

import React, { useState, useEffect } from 'react';
import { Row, Col, Card, CardBody, CardHeader, CardFooter, Label, Button, Input, Spinner } from 'reactstrap';
import Swal from 'sweetalert2';
import settingService from 'services/setting.service';

const BUILTIN_MODELS = [
    { value: 'gpt-5.4', label: 'GPT-5.4 — Mạnh nhất ($2.50 / $15.00 per 1M tokens)' },
    { value: 'gpt-5.4-mini', label: 'GPT-5.4 Mini — Cân bằng ($0.75 / $4.50 per 1M tokens)' },
    { value: 'gpt-5.4-nano', label: 'GPT-5.4 Nano — Nhanh & rẻ ($0.20 / $1.25 per 1M tokens)' },
    { value: 'gpt-4o', label: 'GPT-4o — Legacy' },
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini — Legacy' },
];

const SystemAISettings = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [apiKey, setApiKey] = useState('');
    const [model, setModel] = useState('gpt-5.4-mini');
    const [knowledgeBase, setKnowledgeBase] = useState('');
    const [showKey, setShowKey] = useState(false);

    // Custom Models state
    const [customModels, setCustomModels] = useState([]);
    const [newModelLabel, setNewModelLabel] = useState('');
    const [newModelId, setNewModelId] = useState('');
    const [newModelDesc, setNewModelDesc] = useState('');

    // Track original values to detect changes
    const [originalApiKey, setOriginalApiKey] = useState('');
    const [originalModel, setOriginalModel] = useState('gpt-5.4-mini');
    const [originalKnowledgeBase, setOriginalKnowledgeBase] = useState('');
    const [originalCustomModels, setOriginalCustomModels] = useState('[]');

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const res = await settingService.getAll();
            if (res.data?.success) {
                const settings = res.data.settings;
                const keySetting = settings.find(s => s.key === 'openaiApiKey');
                const modelSetting = settings.find(s => s.key === 'openaiModel');
                const kbSetting = settings.find(s => s.key === 'openaiKnowledgeBase');
                const cmSetting = settings.find(s => s.key === 'openaiCustomModels');

                if (keySetting?.value) {
                    setApiKey(keySetting.value);
                    setOriginalApiKey(keySetting.value);
                }
                if (modelSetting?.value) {
                    setModel(modelSetting.value);
                    setOriginalModel(modelSetting.value);
                }
                if (kbSetting?.value) {
                    setKnowledgeBase(kbSetting.value);
                    setOriginalKnowledgeBase(kbSetting.value);
                }
                if (cmSetting?.value) {
                    const models = Array.isArray(cmSetting.value) ? cmSetting.value : [];
                    setCustomModels(models);
                    setOriginalCustomModels(JSON.stringify(models));
                }
            }
        } catch (err) {
            console.error('Lỗi lấy cài đặt AI:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!apiKey.trim()) {
            Swal.fire('Lỗi', 'Vui lòng nhập API Key', 'error');
            return;
        }

        setSaving(true);
        try {
            const promises = [];
            if (apiKey !== originalApiKey) {
                promises.push(settingService.update('openaiApiKey', apiKey.trim()));
            }
            if (model !== originalModel) {
                promises.push(settingService.update('openaiModel', model));
            }
            if (knowledgeBase !== originalKnowledgeBase) {
                promises.push(settingService.update('openaiKnowledgeBase', knowledgeBase));
            }
            if (JSON.stringify(customModels) !== originalCustomModels) {
                promises.push(settingService.update('openaiCustomModels', customModels));
            }

            if (promises.length === 0) {
                Swal.fire('Thông báo', 'Không có thay đổi nào', 'info');
                setSaving(false);
                return;
            }

            await Promise.all(promises);
            setOriginalApiKey(apiKey.trim());
            setOriginalModel(model);
            setOriginalKnowledgeBase(knowledgeBase);
            setOriginalCustomModels(JSON.stringify(customModels));

            Swal.fire({
                icon: 'success',
                title: 'Đã lưu!',
                text: 'Cấu hình AI đã được cập nhật thành công.',
                timer: 2000,
                showConfirmButton: false
            });
        } catch (err) {
            console.error('Lỗi lưu cài đặt AI:', err);
            Swal.fire('Lỗi', 'Không thể lưu cài đặt. Vui lòng thử lại.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleAddCustomModel = () => {
        if (!newModelLabel.trim() || !newModelId.trim()) {
            Swal.fire('Lỗi', 'Vui lòng nhập tên và Model ID', 'error');
            return;
        }

        // Check duplicate
        if (customModels.some(m => m.modelId === newModelId.trim())) {
            Swal.fire('Lỗi', 'Model ID này đã tồn tại', 'warning');
            return;
        }

        const newModel = {
            id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
            label: newModelLabel.trim(),
            modelId: newModelId.trim(),
            description: newModelDesc.trim(),
            createdAt: new Date().toISOString()
        };

        setCustomModels([...customModels, newModel]);
        setNewModelLabel('');
        setNewModelId('');
        setNewModelDesc('');
    };

    const handleRemoveCustomModel = (modelId) => {
        Swal.fire({
            title: 'Xóa model này?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Xóa',
            cancelButtonText: 'Hủy',
            confirmButtonColor: '#e74c3c'
        }).then((result) => {
            if (result.isConfirmed) {
                setCustomModels(customModels.filter(m => m.id !== modelId));
                // Nếu model mặc định đang chọn model bị xóa → reset về built-in
                const removedModel = customModels.find(m => m.id === modelId);
                if (removedModel && model === removedModel.modelId) {
                    setModel('gpt-5.4-mini');
                }
            }
        });
    };

    const hasChanges = apiKey !== originalApiKey ||
        model !== originalModel ||
        knowledgeBase !== originalKnowledgeBase ||
        JSON.stringify(customModels) !== originalCustomModels;

    if (loading) {
        return (
            <div className="content" style={{ display: 'flex', justifyContent: 'center', paddingTop: '60px' }}>
                <div style={{ color: '#888', fontSize: '18px' }}>
                    <i className="fas fa-sync-alt fa-spin" style={{ marginRight: '8px' }}></i>
                    Đang tải cài đặt...
                </div>
            </div>
        );
    }

    return (
        <div className="content">
            <Row>
                <Col md="8" lg="6">
                    {/* ═══ API Key + Default Model Card ═══ */}
                    <Card style={{
                        borderRadius: '20px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        border: 'none',
                        overflow: 'hidden'
                    }}>
                        <CardHeader style={{
                            background: 'linear-gradient(135deg, #8e44ad 0%, #6c3483 100%)',
                            padding: '20px 24px',
                            borderBottom: 'none'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <i className="fas fa-robot" style={{
                                    fontSize: '32px',
                                    color: '#fff',
                                    opacity: 0.9
                                }}></i>
                                <div style={{ lineHeight: '1.3' }}>
                                    <Label style={{
                                        fontSize: '18px',
                                        color: '#fff',
                                        fontWeight: '700',
                                        marginBottom: '0',
                                        display: 'block'
                                    }}>
                                        OpenAI ChatGPT
                                    </Label>
                                    <span style={{
                                        fontSize: '13px',
                                        color: 'rgba(255,255,255,0.7)'
                                    }}>
                                        Cấu hình AI cho hệ thống
                                    </span>
                                </div>
                            </div>
                        </CardHeader>

                        <CardBody style={{ padding: '24px' }}>
                            {/* API Key */}
                            <div style={{ marginBottom: '20px' }}>
                                <Label style={{
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#2c3e50',
                                    marginBottom: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}>
                                    <i className="fas fa-key" style={{ color: '#8e44ad' }}></i>
                                    API Key
                                </Label>
                                <div style={{ position: 'relative' }}>
                                    <Input
                                        type={showKey ? 'text' : 'password'}
                                        value={apiKey}
                                        onChange={(e) => setApiKey(e.target.value)}
                                        placeholder="sk-proj-..."
                                        style={{
                                            borderRadius: '12px',
                                            padding: '10px 50px 10px 16px',
                                            fontSize: '14px',
                                            border: '2px solid #e9ecef',
                                            transition: 'border-color 0.3s',
                                            fontFamily: showKey ? 'monospace' : 'inherit'
                                        }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowKey(!showKey)}
                                        style={{
                                            position: 'absolute',
                                            right: '12px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            color: '#8898aa',
                                            fontSize: '16px',
                                            padding: '4px'
                                        }}
                                    >
                                        <i className={`fas ${showKey ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                    </button>
                                </div>
                                <small style={{ color: '#8898aa', marginTop: '4px', display: 'block' }}>
                                    Lấy API Key tại <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer"
                                        style={{ color: '#8e44ad' }}>platform.openai.com</a>
                                </small>
                            </div>

                            {/* Model Selection — gộp custom + built-in */}
                            <div style={{ marginBottom: '8px' }}>
                                <Label style={{
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#2c3e50',
                                    marginBottom: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}>
                                    <i className="fas fa-brain" style={{ color: '#8e44ad' }}></i>
                                    Model mặc định
                                </Label>
                                <Input
                                    type="select"
                                    value={model}
                                    onChange={(e) => setModel(e.target.value)}
                                    style={{
                                        borderRadius: '12px',
                                        padding: '10px 16px',
                                        fontSize: '14px',
                                        border: '2px solid #e9ecef',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {customModels.length > 0 && (
                                        <optgroup label="⭐ Custom Fine-Tuned Models">
                                            {customModels.map(m => (
                                                <option key={m.id} value={m.modelId}>⭐ {m.label}</option>
                                            ))}
                                        </optgroup>
                                    )}
                                    <optgroup label="Built-in Models">
                                        {BUILTIN_MODELS.map(m => (
                                            <option key={m.value} value={m.value}>{m.label}</option>
                                        ))}
                                    </optgroup>
                                </Input>
                                <small style={{ color: '#8898aa', marginTop: '4px', display: 'block' }}>
                                    Model này sẽ được sử dụng mặc định cho tất cả chức năng AI trong hệ thống.
                                </small>
                            </div>
                        </CardBody>

                        <CardFooter style={{
                            padding: '16px 24px',
                            borderTop: '1px solid #f0f0f0',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '10px'
                        }}>
                            <Button
                                color="primary"
                                disabled={saving || !hasChanges}
                                onClick={handleSave}
                                style={{
                                    borderRadius: '12px',
                                    padding: '8px 24px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    background: hasChanges ? 'linear-gradient(135deg, #8e44ad 0%, #6c3483 100%)' : '#ccc',
                                    border: 'none',
                                    opacity: saving ? 0.7 : 1
                                }}
                            >
                                {saving ? (
                                    <><Spinner size="sm" style={{ marginRight: '6px' }} /> Đang lưu...</>
                                ) : (
                                    <><i className="fas fa-save" style={{ marginRight: '6px' }}></i> Lưu cấu hình</>
                                )}
                            </Button>
                        </CardFooter>
                    </Card>
                </Col>

                {/* ═══ Custom Models Card ═══ */}
                <Col md="8" lg="6">
                    <Card style={{
                        borderRadius: '20px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        border: 'none',
                        overflow: 'hidden'
                    }}>
                        <CardHeader style={{
                            background: 'linear-gradient(135deg, #e67e22 0%, #d35400 100%)',
                            padding: '20px 24px',
                            borderBottom: 'none'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <i className="fas fa-magic" style={{
                                    fontSize: '32px',
                                    color: '#fff',
                                    opacity: 0.9
                                }}></i>
                                <div style={{ lineHeight: '1.3' }}>
                                    <Label style={{
                                        fontSize: '18px',
                                        color: '#fff',
                                        fontWeight: '700',
                                        marginBottom: '0',
                                        display: 'block'
                                    }}>
                                        Custom Fine-Tuned Models
                                    </Label>
                                    <span style={{
                                        fontSize: '13px',
                                        color: 'rgba(255,255,255,0.7)'
                                    }}>
                                        Quản lý các model AI đã fine-tune cho từng phần mềm
                                    </span>
                                </div>
                            </div>
                        </CardHeader>

                        <CardBody style={{ padding: '24px' }}>
                            {/* Danh sách custom models hiện có */}
                            {customModels.length > 0 ? (
                                <div style={{ marginBottom: '20px' }}>
                                    {customModels.map((cm) => (
                                        <div key={cm.id} style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            padding: '12px 16px',
                                            borderRadius: '12px',
                                            backgroundColor: '#f8f9fa',
                                            marginBottom: '8px',
                                            border: '1px solid #e9ecef'
                                        }}>
                                            <i className="fas fa-star" style={{ color: '#e67e22', fontSize: '16px', flexShrink: 0 }}></i>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontWeight: '600', fontSize: '14px', color: '#2c3e50' }}>
                                                    {cm.label}
                                                </div>
                                                <div style={{
                                                    fontSize: '12px',
                                                    color: '#8898aa',
                                                    fontFamily: 'monospace',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {cm.modelId}
                                                </div>
                                                {cm.description && (
                                                    <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>
                                                        {cm.description}
                                                    </div>
                                                )}
                                            </div>
                                            <button
                                                onClick={() => handleRemoveCustomModel(cm.id)}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    color: '#e74c3c',
                                                    fontSize: '16px',
                                                    padding: '4px 8px',
                                                    borderRadius: '8px',
                                                    flexShrink: 0
                                                }}
                                                title="Xóa model"
                                            >
                                                <i className="fas fa-trash-alt"></i>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div style={{
                                    textAlign: 'center',
                                    padding: '20px',
                                    color: '#8898aa',
                                    fontSize: '14px',
                                    marginBottom: '16px'
                                }}>
                                    <i className="fas fa-inbox" style={{ fontSize: '28px', marginBottom: '8px', display: 'block', opacity: 0.5 }}></i>
                                    Chưa có model custom nào
                                </div>
                            )}

                            {/* Form thêm model mới */}
                            <div style={{
                                padding: '16px',
                                borderRadius: '12px',
                                backgroundColor: '#fff8f0',
                                border: '1px dashed #e67e22'
                            }}>
                                <Label style={{
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    color: '#d35400',
                                    marginBottom: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}>
                                    <i className="fas fa-plus-circle"></i>
                                    Thêm model mới
                                </Label>
                                <Input
                                    type="text"
                                    value={newModelLabel}
                                    onChange={(e) => setNewModelLabel(e.target.value)}
                                    placeholder="Tên hiển thị (VD: DHB Render Fine-Tuned)"
                                    style={{
                                        borderRadius: '10px',
                                        padding: '8px 14px',
                                        fontSize: '13px',
                                        border: '1.5px solid #e9ecef',
                                        marginBottom: '8px'
                                    }}
                                />
                                <Input
                                    type="text"
                                    value={newModelId}
                                    onChange={(e) => setNewModelId(e.target.value)}
                                    placeholder="Model ID (VD: ft:gpt-4.1-mini-2025-04-14:personal::DTnRZdu9)"
                                    style={{
                                        borderRadius: '10px',
                                        padding: '8px 14px',
                                        fontSize: '13px',
                                        border: '1.5px solid #e9ecef',
                                        marginBottom: '8px',
                                        fontFamily: 'monospace'
                                    }}
                                />
                                <Input
                                    type="text"
                                    value={newModelDesc}
                                    onChange={(e) => setNewModelDesc(e.target.value)}
                                    placeholder="Mô tả (VD: Chuyên phân tích lỗi DHB Render)"
                                    style={{
                                        borderRadius: '10px',
                                        padding: '8px 14px',
                                        fontSize: '13px',
                                        border: '1.5px solid #e9ecef',
                                        marginBottom: '12px'
                                    }}
                                />
                                <Button
                                    color="warning"
                                    onClick={handleAddCustomModel}
                                    style={{
                                        width: '100%',
                                        borderRadius: '10px',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        background: 'linear-gradient(135deg, #e67e22 0%, #d35400 100%)',
                                        border: 'none',
                                        color: '#fff'
                                    }}
                                >
                                    <i className="fas fa-plus" style={{ marginRight: '6px' }}></i>
                                    Thêm model
                                </Button>
                            </div>
                        </CardBody>

                        <CardFooter style={{
                            padding: '16px 24px',
                            borderTop: '1px solid #f0f0f0',
                            display: 'flex',
                            justifyContent: 'flex-end'
                        }}>
                            <Button
                                color="primary"
                                disabled={saving || !hasChanges}
                                onClick={handleSave}
                                style={{
                                    borderRadius: '12px',
                                    padding: '8px 24px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    background: hasChanges ? 'linear-gradient(135deg, #e67e22 0%, #d35400 100%)' : '#ccc',
                                    border: 'none',
                                    opacity: saving ? 0.7 : 1
                                }}
                            >
                                {saving ? (
                                    <><Spinner size="sm" style={{ marginRight: '6px' }} /> Đang lưu...</>
                                ) : (
                                    <><i className="fas fa-save" style={{ marginRight: '6px' }}></i> Lưu cấu hình</>
                                )}
                            </Button>
                        </CardFooter>
                    </Card>
                </Col>
            </Row>

            {/* Knowledge Base Card */}
            <Row>
                <Col md="12" lg="10">
                    <Card style={{
                        borderRadius: '20px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        border: 'none',
                        overflow: 'hidden'
                    }}>
                        <CardHeader style={{
                            background: 'linear-gradient(135deg, #2c3e50 0%, #1a252f 100%)',
                            padding: '20px 24px',
                            borderBottom: 'none'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <i className="fas fa-book" style={{
                                    fontSize: '32px',
                                    color: '#fff',
                                    opacity: 0.9
                                }}></i>
                                <div style={{ lineHeight: '1.3' }}>
                                    <Label style={{
                                        fontSize: '18px',
                                        color: '#fff',
                                        fontWeight: '700',
                                        marginBottom: '0',
                                        display: 'block'
                                    }}>
                                        Cơ sở kiến thức AI
                                    </Label>
                                    <span style={{
                                        fontSize: '13px',
                                        color: 'rgba(255,255,255,0.7)'
                                    }}>
                                        Dữ liệu này sẽ được nạp vào AI khi phân tích báo cáo lỗi
                                    </span>
                                </div>
                            </div>
                        </CardHeader>

                        <CardBody style={{ padding: '24px' }}>
                            <small style={{ color: '#8898aa', display: 'block', marginBottom: '12px' }}>
                                Nhập thông tin về sản phẩm, các lỗi thường gặp, cách khắc phục, tính năng chính...
                                AI sẽ dựa vào kiến thức này để đưa ra phản hồi chính xác hơn cho khách hàng.
                            </small>
                            <Input
                                type="textarea"
                                value={knowledgeBase}
                                onChange={(e) => setKnowledgeBase(e.target.value)}
                                placeholder={"Ví dụ:\n\n## DHB Render\n- Phần mềm tạo video tự động trên Windows\n- Yêu cầu: .NET 8, Windows 10/11\n- Lỗi thường gặp:\n  + \"Access Denied\" → Chạy với quyền Admin\n  + \"FFmpeg not found\" → Cài FFmpeg hoặc chọn lại đường dẫn\n  + Crash khi render → Kiểm tra RAM, GPU driver\n\n## DHB Reup\n- Phần mềm reup video lên mạng xã hội\n- ..."}
                                style={{
                                    borderRadius: '12px',
                                    padding: '16px',
                                    fontSize: '14px',
                                    border: '2px solid #e9ecef',
                                    minHeight: '300px',
                                    fontFamily: 'monospace',
                                    lineHeight: '1.6',
                                    resize: 'vertical'
                                }}
                            />
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
                                <small style={{ color: '#8898aa' }}>
                                    Hỗ trợ Markdown. Dữ liệu càng chi tiết, AI phản hồi càng chính xác.
                                </small>
                                <small style={{ color: knowledgeBase.length > 8000 ? '#e74c3c' : '#8898aa' }}>
                                    {knowledgeBase.length} ký tự
                                </small>
                            </div>
                        </CardBody>

                        <CardFooter style={{
                            padding: '16px 24px',
                            borderTop: '1px solid #f0f0f0',
                            display: 'flex',
                            justifyContent: 'flex-end'
                        }}>
                            <Button
                                color="primary"
                                disabled={saving || !hasChanges}
                                onClick={handleSave}
                                style={{
                                    borderRadius: '12px',
                                    padding: '8px 24px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    background: hasChanges ? 'linear-gradient(135deg, #2c3e50 0%, #1a252f 100%)' : '#ccc',
                                    border: 'none',
                                    opacity: saving ? 0.7 : 1
                                }}
                            >
                                {saving ? (
                                    <><Spinner size="sm" style={{ marginRight: '6px' }} /> Đang lưu...</>
                                ) : (
                                    <><i className="fas fa-save" style={{ marginRight: '6px' }}></i> Lưu tất cả</>
                                )}
                            </Button>
                        </CardFooter>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default SystemAISettings;
